import {
  InvestmentRoundingSetting,
  ReverseVestingOnOption,
  roundInvestmentShares,
  validateReverseVestingSchedule,
} from 'shared';
import { z } from 'zod';

import { EventFormType, ValuationSelect } from '../../../types/events.types';
import { hasUniqueFileDisplayNames } from '../../../utils/file-upload-duplicate-names.util';
import { getTranslation } from '../../../utils/get-translation.util';
import { vestingScheduleIssueKey } from '../../../utils/vesting-schedule-errors.util';

export const MAX_FILE_SIZE = 1024 * 1024 * 10;
export const MAX_EVENT_ITEMS = 100;
export const MAX_FUNDRAISING_INVESTMENT_ITEMS = 200;
export const MAX_EVENT_UPLOAD_FILES = 20;
export const MAX_EVENT_ADDITIONAL_NOTES_LENGTH = 3000;

export const defaultSharePrice = 1.000000000000001;

export const ACCEPTED_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
];

export const ACCEPTED_GRANT_FILE_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const fileSchemaMessage = getTranslation('FILE_SIZE_TOO_LARGE');

const grantFileSchema = z
  .custom<File>()
  .refine(
    (files) => ACCEPTED_GRANT_FILE_MIME_TYPES.includes(files?.type),
    getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
  )
  .refine((files) => {
    return files?.size <= MAX_FILE_SIZE;
  }, fileSchemaMessage);

const subFileEntrySchema = z.object({
  docLink: z.string(),
  loadProgress: z.number().max(100),
  abort: z.function(),
  id: z.string(),
  doc: z
    .custom<File>()
    .or(
      z.object({
        size: z.number(),
        type: z.string(),
        name: z.string(),
      }),
    )
    .refine(
      ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
      getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
    )
    .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
});

export const subFilesSchema = subFileEntrySchema
  .array()
  .refine(hasUniqueFileDisplayNames, { message: getTranslation('FILE_EXISTS') });

const t = getTranslation;

const isValidFormDate = (value?: Date): value is Date =>
  !!value && !Number.isNaN(value.getTime()) && value.getTime() !== 0;

// Empty numeric inputs must stay `undefined` rather than coercing to 0, otherwise a blank
// mandatory field would silently pass the "is it filled in?" check below.
const optionalFormNumber = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }).optional(),
);

// Every field is optional at this level so that both the OFF state and a half-filled ON state
// still parse; requiredness is decided by refineReverseVestingRow, which knows about the toggle.
const reverseVestingSchema = z.object({
  enabled: z.boolean().optional(),
  shares: optionalFormNumber,
  startDate: z.coerce.date().optional(),
  vestingOn: z.enum(ReverseVestingOnOption).optional(),
  durationMonths: optionalFormNumber,
  frequencyMonths: optionalFormNumber,
  cliffMonths: optionalFormNumber,
});

export type ReverseVestingFormData = z.infer<typeof reverseVestingSchema>;

/**
 * Shared by the Company Incorporation and Share Issuance row schemas. Attached to the *row* rather
 * than the step object on purpose: the step schemas are `.merge()`d, and `.merge` on a ZodEffects
 * is a type error.
 */
const refineReverseVestingRow = (
  row: { reverseVesting?: ReverseVestingFormData; shares?: number },
  ctx: z.RefinementCtx,
): void => {
  const { reverseVesting } = row;
  if (!reverseVesting?.enabled) return;

  const required = (path: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== '') return;
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('REQUIRED'),
      path: ['reverseVesting', path],
    });
  };

  /**
   * `required` accepts 0, and the shared schedule validator deliberately treats 0 as "not filled
   * in", so without this a zero sailed through the form untouched. A blank cliff — not a zero — is
   * how "no cliff" is expressed, so 0 is a mistake in every one of these fields; `undefined` is
   * skipped here and left to `required`, which keeps an empty field to a single message.
   */
  const positive = (path: string, value?: number) => {
    if (value === undefined || value > 0) return;
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('MUST_BE_GREATER_THAN_ZERO'),
      path: ['reverseVesting', path],
    });
  };

  /**
   * The backend schema types all four as `.int()`, so a fractional figure is a 400 with no field
   * attached to it. Caught here instead, next to the input the operator typed it into — a 7.5-month
   * duration otherwise passed the form (7.5 % 2.5 === 0 divides cleanly) and failed on submit.
   */
  const whole = (path: string, value?: number) => {
    if (value === undefined || Number.isInteger(value)) return;
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('MUST_BE_A_WHOLE_NUMBER'),
      path: ['reverseVesting', path],
    });
  };

  required('shares', reverseVesting.shares);
  required('vestingOn', reverseVesting.vestingOn);
  required('durationMonths', reverseVesting.durationMonths);
  required('frequencyMonths', reverseVesting.frequencyMonths);

  positive('shares', reverseVesting.shares);
  positive('durationMonths', reverseVesting.durationMonths);
  positive('frequencyMonths', reverseVesting.frequencyMonths);
  positive('cliffMonths', reverseVesting.cliffMonths);

  whole('shares', reverseVesting.shares);
  whole('durationMonths', reverseVesting.durationMonths);
  whole('frequencyMonths', reverseVesting.frequencyMonths);
  whole('cliffMonths', reverseVesting.cliffMonths);

  if (!isValidFormDate(reverseVesting.startDate)) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('REQUIRED'),
      path: ['reverseVesting', 'startDate'],
    });
  }

  const issuedShares = Number(row.shares ?? 0);
  if (reverseVesting.shares !== undefined && reverseVesting.shares > issuedShares) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('AMOUNT_CANNOT_EXCEED_ISSUED_SHARES'),
      path: ['reverseVesting', 'shares'],
    });
  }

  validateReverseVestingSchedule(reverseVesting).forEach(({ field, issue }) => {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation(vestingScheduleIssueKey[issue]),
      path: ['reverseVesting', field],
    });
  });
};

/**
 * Just the reverse-vesting part of an issuance row. Zod skips a row's `superRefine` while any of
 * its base fields fail, so the full row schema cannot say whether the *schedule* is incomplete on a
 * row that is also missing its stakeholder — this one can.
 */
export const reverseVestingRowSchema = z
  .object({
    reverseVesting: reverseVestingSchema.optional(),
    shares: optionalFormNumber,
  })
  .superRefine(refineReverseVestingRow);

export const initialStep = z.object({
  type: z.enum(EventFormType),
  id: z.string().optional(),
});

export const GrantSignatureOption = {
  SIGNATURE: 'signature',
  WITHOUT_SIGNATURE: 'without-signature',
} as const;

export type GrantSignatureOptionType =
  (typeof GrantSignatureOption)[keyof typeof GrantSignatureOption];

const sharePlanShape = z.object({
  id: z.string().trim(),
  name: z.string().trim(),
  pool: z.object({ id: z.string().trim() }),
});

const grantItemRowSchema = z
  .object({
    id: z.string().optional(),
    grantDate: z.coerce.date(),
    stakeholder: z.object({
      email: z.string().trim().optional(),
      fullName: z
        .string()
        .trim()
        .min(1, { message: getTranslation('REQUIRED') }),
      id: z
        .string()
        .trim()
        .min(1, { message: getTranslation('REQUIRED') }),
    }),
    sharePlan: sharePlanShape.optional(),
    shares: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .min(1, { message: getTranslation('REQUIRED') }),
    initialShares: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .optional(),
    initialSharePlanId: z.string().trim().optional(),
    balance: z.coerce.number({
      message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
    }),
    files: subFilesSchema.optional(),
  })
  .refine(
    ({ shares, balance }) => {
      return shares <= balance;
    },
    {
      message: getTranslation('SHARES_MUST_BE_LESS_THAN_BALANCE'),
      path: ['shares'],
    },
  )
  .refine(
    ({ grantDate }) => {
      return grantDate.getTime() !== new Date(+0).getTime();
    },
    {
      message: 'Invalid grant date',
      path: ['grantDate'],
    },
  );

export const grantItemsArraySchema = grantItemRowSchema
  .array()
  .min(1, { message: getTranslation('REQUIRED') })
  .max(100, {
    message: getTranslation('ITEMS_MAXIMUM', { count: 100 }),
  });

const grant = z.object({
  enabled: z.boolean(),
  sharePlan: sharePlanShape.optional(),
  signatureOption: z
    .enum([GrantSignatureOption.SIGNATURE, GrantSignatureOption.WITHOUT_SIGNATURE])
    .optional(),
  selectedTemplateId: z.string().trim().optional(),
  signatoryAssignments: z
    .record(
      z.string().trim(),
      z.object({ id: z.string().trim(), fullName: z.string().trim() }).nullable(),
    )
    .optional(),
  grantItems: grantItemsArraySchema,
});

const bulkGrant = z.object({
  enabled: z.boolean(),
  file: grantFileSchema,
});

// Both event pickers are the same valuation combobox: with nothing selected the whole object is
// null, and that object-level issue is what the combobox renders. Name the two ways forward so the
// user is not left with Zod's "expected object, received null".
const valuationSelectionSchema = (emptyMessage: string, minSharePrice: number) =>
  z
    .object(
      {
        name: z
          .string()
          .trim()
          .min(1, { message: getTranslation('REQUIRED') })
          .max(50, { message: getTranslation('CHARACTER_LIMIT_IS_COUNT', { count: 50 }) }),
        id: z
          .string()
          .trim()
          .min(1, { message: getTranslation('REQUIRED') }),
        sharePrice: z.coerce
          .number()
          .min(minSharePrice, { message: getTranslation('REQUIRED') })
          .default(defaultSharePrice),
        issuedSharesOnStart: z.coerce
          .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
          .default(0),
      },
      { error: emptyMessage },
    )
    .refine(
      ({ sharePrice }) => {
        return sharePrice !== defaultSharePrice;
      },
      {
        message: '',
        path: ['sharePrice'],
      },
    );

const fundraisingRoundOne = z
  .object({
    enabled: z.boolean(),
    isOpen: z.boolean(),
    roundingSetting: z.enum(InvestmentRoundingSetting).default(InvestmentRoundingSetting.DEFAULT),
    event: valuationSelectionSchema(getTranslation('SELECT_OR_ADD_ROUND_NAME'), 0.0001),
    date: z.coerce.date().optional(),
    closeDate: z.coerce.date().optional(),
  })
  .superRefine((val, ctx) => {
    const { event, date: openDate, closeDate, isOpen } = val;

    if (event.sharePrice === defaultSharePrice) {
      ctx.addIssue({
        code: 'custom',
        path: ['event', 'sharePrice'],
        message: t('REQUIRED'),
      });
    }

    // The opening date is required whether or not the round is open. Aliasing it to the closing
    // date while closed reported a missing closing date on the `date` field, which the form hides
    // in that state — the toast fired with nothing highlighted to fix.
    if (!isValidFormDate(openDate)) {
      ctx.addIssue({
        code: 'custom',
        path: ['date'],
        message: t('INVALID_DATE'),
      });
    }

    if (!isOpen && !closeDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['closeDate'],
        message: t('REQUIRED'),
      });
    }

    if (closeDate) {
      if (!isValidFormDate(closeDate)) {
        ctx.addIssue({
          code: 'custom',
          path: ['closeDate'],
          message: t('INVALID_DATE'),
        });
      } else if (isValidFormDate(openDate) && closeDate.getTime() < openDate.getTime()) {
        ctx.addIssue({
          code: 'custom',
          path: ['closeDate'],
          message: t('CLOSE_DATE_MUST_BE_AFTER_OPEN_DATE'),
        });
      }
    }
  });

const fundraisingRoundTwo = z
  .object({
    closeDate: z.coerce.date().optional(),
    investmentItems: z
      .array(
        z
          .object({
            id: z.string().optional(),
            sharePrice: z.coerce
              .number()
              .min(0, { message: getTranslation('REQUIRED') })
              .default(1),
            stakeholder: z.object({
              fullName: z
                .string()
                .trim()
                .min(1, { message: getTranslation('REQUIRED') }),
              id: z
                .string()
                .trim()
                .min(1, { message: getTranslation('REQUIRED') }),
            }),
            investment: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(1, { message: getTranslation('REQUIRED') }),
            shareClass: z.object({
              name: z
                .string()
                .trim()
                .min(1, { message: getTranslation('REQUIRED') }),
              id: z
                .string()
                .trim()
                .min(1, { message: getTranslation('REQUIRED') }),
            }),
            investmentDate: z.coerce.date(),
            files: subFilesSchema.optional(),
          })
          .refine(
            ({ investmentDate }) => {
              return investmentDate.getTime() !== new Date(+0).getTime();
            },
            {
              message: getTranslation('INVALID_INVESTMENT_DATE'),
              path: ['investmentDate'],
            },
          ),
      )
      .min(1, { message: getTranslation('REQUIRED') })
      .max(MAX_FUNDRAISING_INVESTMENT_ITEMS, {
        message: getTranslation('ITEMS_MAXIMUM', { count: MAX_FUNDRAISING_INVESTMENT_ITEMS }),
      }),
  })
  .superRefine((data, ctx) => {
    const { closeDate, investmentItems } = data;

    if (closeDate) {
      investmentItems.forEach((item, index) => {
        if (item.investmentDate > closeDate) {
          ctx.addIssue({
            code: 'custom',
            message: getTranslation('INVESTMENT_DATE_MUST_BE_BEFORE_CLOSE_DATE'),
            path: ['investmentItems', index, 'investmentDate'],
          });
        }
      });
    }
  });

const issueSharesOne = z.object({
  enabled: z.boolean(),
  event: valuationSelectionSchema(getTranslation('SELECT_OR_ADD_EVENT_NAME'), 0),
  date: z.coerce.date(),
});

export const shareIssuanceItemSchema = z
  .object({
    id: z.string().optional(),
    // Stakeholder the persisted row was loaded with. The edit flow freezes a row only when the
    // saved holder is terminated — re-pointing a row at someone else must not inherit the freeze.
    initialStakeholderId: z.string().optional(),
    stakeholder: z.object({
      fullName: z
        .string()
        .trim()
        .min(1, { message: getTranslation('REQUIRED') }),
      id: z
        .string()
        .trim()
        .min(1, { message: getTranslation('REQUIRED') }),
      // See the Company Incorporation stakeholder schema — same reverse-vesting freeze.
      isTerminated: z.boolean().optional(),
    }),
    shareClass: z.object({
      name: z
        .string()
        .trim()
        .min(1, { message: getTranslation('REQUIRED') }),
      id: z
        .string()
        .trim()
        .min(1, { message: getTranslation('REQUIRED') }),
    }),
    shares: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .min(1, { message: getTranslation('REQUIRED') }),
    files: subFilesSchema.optional(),
    reverseVesting: reverseVestingSchema.optional(),
  })
  .superRefine(refineReverseVestingRow);

const issueSharesTwo = z.object({
  issuanceItems: z
    .array(shareIssuanceItemSchema)
    .min(1, { message: getTranslation('REQUIRED') })
    .max(100, { message: getTranslation('ITEMS_MAXIMUM', { count: 100 }) }),
});

const buyBack = z.object({
  enabled: z.boolean(),
  buyBackItems: z
    .array(
      z
        .object({
          id: z.string().optional(),
          date: z.coerce.date(),
          balance: z.coerce.number({
            message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
          }),
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          shareClass: z.object({
            name: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          shares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
          initialShares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .optional(),
          initialStakeholderId: z.string().trim().optional(),
          initialShareClassId: z.string().trim().optional(),
          files: subFilesSchema.optional(),
        })
        .refine(
          ({ shares, balance }) => {
            return shares <= balance;
          },
          {
            message: getTranslation('SHARES_MUST_BE_LESS_THAN_BALANCE'),
            path: ['shares'],
          },
        )
        .refine(
          ({ date }) => {
            return date.getTime() !== new Date(+0).getTime();
          },
          {
            message: getTranslation('INVALID_DATE'),
            path: ['date'],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(100, { message: getTranslation('ITEMS_MAXIMUM', { count: 100 }) }),
});

const secondaries = z.object({
  enabled: z.boolean(),
  secondariesItems: z
    .array(
      z
        .object({
          id: z.string().optional(),
          date: z.coerce.date(),
          balance: z.coerce.number({
            message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
          }),
          stakeholderFrom: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          stakeholderTo: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          shareClass: z.object({
            name: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          shares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
          shareValue: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0.0001, { message: getTranslation('REQUIRED') }),
          initialShares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .optional(),
          initialStakeholderFromId: z.string().trim().optional(),
          initialStakeholderToId: z.string().trim().optional(),
          initialShareClassId: z.string().trim().optional(),
          files: subFilesSchema.optional(),
        })
        .refine(
          ({ shares, balance }) => {
            return shares <= balance;
          },
          {
            message: getTranslation('SHARES_MUST_BE_LESS_THAN_BALANCE'),
            path: ['shares'],
          },
        )
        .refine(
          ({ date }) => {
            return date.getTime() !== new Date(+0).getTime();
          },
          {
            message: getTranslation('INVALID_DATE'),
            path: ['date'],
          },
        )
        .refine(({ stakeholderFrom, stakeholderTo }) => stakeholderFrom.id !== stakeholderTo.id, {
          message: getTranslation('STAKEHOLDERS_MUST_BE_DIFFERENT'),
          path: ['stakeholderTo', 'id'],
        }),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(100, { message: getTranslation('ITEMS_MAXIMUM', { count: 100 }) }),
});

const classConversion = z.object({
  enabled: z.boolean(),
  classConversionItems: z
    .array(
      z
        .object({
          id: z.string().optional(),
          date: z.coerce.date(),
          balance: z.coerce.number({
            message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
          }),
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          shareClassFrom: z.object({
            name: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            conversionRatio: z.number(),
          }),
          shareClassTo: z.object({
            name: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          shares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
          initialShares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .optional(),
          initialStakeholderId: z.string().trim().optional(),
          initialShareClassFromId: z.string().trim().optional(),
          initialShareClassToId: z.string().trim().optional(),
        })
        .refine(
          ({ shares, balance }) => {
            return shares <= balance;
          },
          {
            message: getTranslation('SHARES_MUST_BE_LESS_THAN_BALANCE'),
            path: ['shares'],
          },
        )
        .refine(
          ({ date }) => {
            return date.getTime() !== new Date(+0).getTime();
          },
          {
            message: getTranslation('INVALID_DATE'),
            path: ['date'],
          },
        )
        .refine(({ shareClassFrom, shareClassTo }) => shareClassFrom.id !== shareClassTo.id, {
          message: getTranslation('SHARE_CLASSES_MUST_BE_DIFFERENT'),
          path: ['shareClassTo', 'id'],
        }),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(100, { message: getTranslation('ITEMS_MAXIMUM', { count: 100 }) }),
});

const valuation = z
  .object({
    enabled: z.boolean(),
    name: z
      .string()
      .trim()
      .min(1, { message: getTranslation('REQUIRED') })
      .max(50, { message: getTranslation('CHARACTER_LIMIT_IS_COUNT', { count: 50 }) }),
    date: z.coerce.date(),
    sharePrice: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .min(0, { message: getTranslation('REQUIRED') }),
    issuedSharesOnStart: z.coerce
      .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
      .optional(),
    valuationSelect: z.enum(ValuationSelect),
    // Empty input coerces to undefined so we can distinguish it from an explicit 0 (0 is a valid value).
    preMoneyValuation: z.preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.coerce
        .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
        .min(0, { message: getTranslation('REQUIRED') })
        .optional(),
    ),
  })
  .superRefine((data, ctx) => {
    // In Pre-money mode the value is user-entered and required — an empty field must block the step (0 is allowed).
    if (
      data.valuationSelect === ValuationSelect.PRE_MONEY_VALUATION &&
      data.preMoneyValuation === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: getTranslation('REQUIRED'),
        path: ['preMoneyValuation'],
      });
    }
  });

const grantForBulkContext = z.object({
  sharePlan: sharePlanShape.optional(),
  signatureOption: z
    .enum([GrantSignatureOption.SIGNATURE, GrantSignatureOption.WITHOUT_SIGNATURE])
    .optional(),
});

export type GrantFormData = z.infer<typeof grant>;

const stepOneEventDetails = z.object({
  grant: grant.partial().optional(),
  'bulk-import-grant': bulkGrant.optional(),
  'fundraising-round': fundraisingRoundOne.optional(),
  'share-issuance': issueSharesOne.optional(),
  buyback: buyBack.optional(),
  secondaries: secondaries.optional(),
  conversion: classConversion.optional(),
  valuation: valuation.optional(),
});

export const stepOne = z
  .object({
    eventDetails: stepOneEventDetails,
  })
  .superRefine((data, ctx) => {
    const bulkEnabled = data.eventDetails?.['bulk-import-grant']?.enabled;
    const grantData = data.eventDetails?.grant;

    if (bulkEnabled) {
      if (grantData) {
        const result = grantForBulkContext.safeParse(grantData);
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            const basePath = ['eventDetails', 'grant'];
            const fullPath = issue.path ? [...basePath, ...issue.path] : basePath;
            ctx.addIssue({ ...issue, path: fullPath });
          });
        }
      }
      const bulkResult = bulkGrant.safeParse(data.eventDetails['bulk-import-grant']);
      if (!bulkResult.success) {
        bulkResult.error.issues.forEach((issue) => {
          const basePath = ['eventDetails', 'bulk-import-grant'];
          const fullPath = issue.path ? [...basePath, ...issue.path] : basePath;
          ctx.addIssue({ ...issue, path: fullPath });
        });
      }
      return;
    }

    if (grantData?.enabled) {
      const result = grant.safeParse(grantData);
      if (!result.success) {
        result.error.issues.forEach((issue) => {
          const basePath = ['eventDetails', 'grant'];
          const fullPath = issue.path ? [...basePath, ...issue.path] : basePath;
          ctx.addIssue({ ...issue, path: fullPath });
        });
      }
    }
  });

export const bulkStepOneSchema = z.object({
  eventDetails: z.object({
    'bulk-import-grant': bulkGrant,
  }),
});

export const optionalStep = z
  .object({
    'fundraising-round': fundraisingRoundTwo.optional(),
    'share-issuance': issueSharesTwo.optional(),
  })
  .optional();

export const stepTwo = z
  .object({
    additionalNotes: z
      .string()
      .max(
        MAX_EVENT_ADDITIONAL_NOTES_LENGTH,
        getTranslation('MAXIMUM_CHARACTERS', { count: MAX_EVENT_ADDITIONAL_NOTES_LENGTH }),
      )
      .optional(),
    files: subFileEntrySchema
      .array()
      .refine((files) => hasUniqueFileDisplayNames(files), {
        message: getTranslation('FILE_EXISTS'),
      })
      .optional(),
  })
  .optional();

const companyIncorporationStakeholderSchema = z.object({
  id: z.string().trim().min(1, getTranslation('REQUIRED')),
  fullName: z.string().trim().min(1, getTranslation('REQUIRED')),
  // Carried on the selection so the reverse-vesting block can freeze itself for a terminated
  // stakeholder on creation too, where there is no saved event to read the flag from.
  isTerminated: z.boolean().optional(),
});

const companyIncorporationShareClassSchema = z.object({
  id: z.string().trim().min(1, getTranslation('REQUIRED')),
  name: z.string().trim().min(1, getTranslation('REQUIRED')),
});

export const companyIncorporationIssuanceSchema = z
  .object({
    id: z.string().optional(),
    // See the Share Issuance row schema — same terminated-stakeholder freeze key.
    initialStakeholderId: z.string().optional(),
    stakeholder: companyIncorporationStakeholderSchema,
    shareClass: companyIncorporationShareClassSchema,
    shares: z.coerce.number().int().positive(getTranslation('REQUIRED')),
    files: subFilesSchema.optional(),
    reverseVesting: reverseVestingSchema.optional(),
  })
  .superRefine(refineReverseVestingRow);

export const companyIncorporationStepOneSchema = z.object({
  incorporationDate: z.coerce.date(),
  parValue: z
    .string()
    .trim()
    .min(1, getTranslation('REQUIRED'))
    .refine(
      (value) => /^(0|[1-9]\d*)(\.\d{1,7})?$/.test(value),
      getTranslation('INVALID_PAR_VALUE'),
    ),
});

export const companyIncorporationStepTwoSchema = z.object({
  issuances: z
    .array(companyIncorporationIssuanceSchema)
    .min(1)
    .max(MAX_EVENT_ITEMS, {
      message: getTranslation('ITEMS_MAXIMUM', { count: MAX_EVENT_ITEMS }),
    }),
});

export const companyIncorporationStepThreeSchema = z.object({
  files: subFileEntrySchema
    .array()
    .max(MAX_EVENT_UPLOAD_FILES)
    .refine((files) => hasUniqueFileDisplayNames(files), {
      message: getTranslation('FILE_EXISTS'),
    })
    .optional(),
  additionalNotes: z
    .string()
    .max(
      MAX_EVENT_ADDITIONAL_NOTES_LENGTH,
      getTranslation('MAXIMUM_CHARACTERS', { count: MAX_EVENT_ADDITIONAL_NOTES_LENGTH }),
    )
    .optional(),
});

export const companyIncorporationSchema = companyIncorporationStepOneSchema
  .merge(companyIncorporationStepTwoSchema)
  .merge(companyIncorporationStepThreeSchema);

export const formSchema = z
  .object({
    initialStep,
    optionalStep,
    stepOne,
    stepTwo,
    companyIncorporation: companyIncorporationSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const fundraisingStep = data.stepOne?.eventDetails?.['fundraising-round'];
    const investmentItems = data.optionalStep?.['fundraising-round']?.investmentItems;

    if (!fundraisingStep?.enabled || !investmentItems?.length) {
      return;
    }

    const sharePrice = fundraisingStep.event.sharePrice;
    const roundingSetting = fundraisingStep.roundingSetting ?? InvestmentRoundingSetting.DEFAULT;

    investmentItems.forEach((item, index) => {
      const shares = roundInvestmentShares(item.investment, sharePrice, roundingSetting);

      if (shares < 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'Investment must be greater than share price',
          path: ['optionalStep', 'fundraising-round', 'investmentItems', index, 'investment'],
        });
      }
    });
  });

export type FormSchema = z.infer<typeof formSchema>;
